import { PlatformAvailableSubscriptionPlansService } from '../../../../api/api-services/platform-available-subscription-plans.service';
import { ApplySubscriptionPlanService } from '../../../../api/api-services/apply-subscription-plan.service';
import { PlatformUserSubscriptionService } from '../../../../api/api-services/platform-user-subscription.service';
import { IPlatformSubscriptionPlanVm } from '../../../../api/platform-subscription-plan-vm.model';
import { IPlatformUserAccountVm } from '../../../../api/platform-user-account-vm.model';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { Component, OnInit, inject } from '@angular/core';
import { MessageService } from 'primeng/api';

export interface IAssignedSubscriptionResult {
  subscriptionPlan: string;
  subscriptionPlanActiveTo?: Date;
}

/** 'assign' picks a plan and its end date, 'validTo' only moves the end date of the plan the user already has. */
export type UsersSubscriptionDialogMode = 'assign' | 'validTo';

@Component({
  selector: 'app-users-add-subscriptions',
  templateUrl: './users-add-subscriptions.component.html',
  styleUrl: './users-add-subscriptions.component.scss',
})
export class UsersAddSubscriptionsComponent implements OnInit {
  private _plansService = inject(PlatformAvailableSubscriptionPlansService);
  private _applySubscriptionPlanService = inject(ApplySubscriptionPlanService);
  private _platformUserSubscriptionService = inject(PlatformUserSubscriptionService);
  private _dynamicDialogRef = inject(DynamicDialogRef);
  private _config = inject(DynamicDialogConfig);
  private _messageService = inject(MessageService);

  public user: IPlatformUserAccountVm = this._config.data.user;
  public mode: UsersSubscriptionDialogMode = this._config.data.mode ?? 'assign';

  public plans: IPlatformSubscriptionPlanVm[] = [];
  public plansLoading = false;
  public selectedPlan: IPlatformSubscriptionPlanVm | null = null;
  public validTo: Date | null = null;
  public minimumDate = new Date();
  public isSaving = false;
  /** Plan applied in this dialog whose end date has not been saved yet. */
  private _assignedPlanName: string | null = null;

  get planName(): string {
    return this._assignedPlanName ?? this.user.subscriptionPlan;
  }

  get validToInPast(): boolean {
    return !!this.validTo && this.endOfDay(this.validTo) < new Date();
  }

  ngOnInit(): void {
    if (this.mode === 'validTo') {
      const activeTo = this.user.subscriptionPlanActiveTo ? new Date(this.user.subscriptionPlanActiveTo) : null;
      this.validTo = activeTo && activeTo.getFullYear() > 1 ? activeTo : null;
      return;
    }
    this.plansLoading = true;
    this._plansService.get('pl').subscribe({
      next: (plans) => {
        this.plans = plans;
        this.plansLoading = false;
      },
      error: () => {
        this.plansLoading = false;
        this._messageService.add({
          severity: 'error',
          summary: 'Błąd',
          detail: 'Nie udało się pobrać listy abonamentów',
        });
      },
    });
  }

  onPlanSelected(): void {
    if (!this.selectedPlan) {
      return;
    }
    const suggested = new Date();
    suggested.setMonth(suggested.getMonth() + (this.selectedPlan.interval ?? 0));
    suggested.setDate(suggested.getDate() + (this.selectedPlan.intervalDays ?? 0));
    this.validTo = suggested;
  }

  save(): void {
    if (!this.validTo || this.isSaving) {
      return;
    }
    if (!this.user.companyGuid) {
      this._messageService.add({
        severity: 'error',
        summary: 'Błąd',
        detail: 'API nie zwróciło identyfikatora firmy użytkownika — wymagana aktualizacja backendu',
      });
      return;
    }
    if (this.mode === 'validTo') {
      this.saveValidTo();
    } else {
      this.assignSubscription();
    }
  }

  closeDialog(): void {
    // The plan is already applied, only with the end date from the plan interval
    const result: IAssignedSubscriptionResult | undefined = this._assignedPlanName
      ? { subscriptionPlan: this._assignedPlanName }
      : undefined;
    this._dynamicDialogRef.close(result);
  }

  private assignSubscription(): void {
    if (!this.selectedPlan) {
      return;
    }
    const plan = this.selectedPlan;
    this.isSaving = true;
    this._applySubscriptionPlanService
      .post({
        subscriptionPlanGuid: plan.guid,
        companyGuid: this.user.companyGuid!,
        startingFrom: new Date(),
        validTo: this.endOfDay(this.validTo!),
      })
      .subscribe({
        next: (result) => {
          if (result.success) {
            // POST /ApplySubscriptionPlan sets the end date from the plan interval, the chosen date is saved separately
            this._assignedPlanName = plan.planName;
            this.saveValidTo();
          } else {
            this.isSaving = false;
            this._messageService.add({
              severity: 'error',
              summary: 'Błąd',
              detail: result.message || 'Nie udało się przydzielić abonamentu',
            });
          }
        },
        error: () => {
          this.isSaving = false;
          this._messageService.add({
            severity: 'error',
            summary: 'Błąd',
            detail: 'Nie udało się przydzielić abonamentu',
          });
        },
      });
  }

  private saveValidTo(): void {
    // Subscription stays active through the whole selected day
    const validTo = this.endOfDay(this.validTo!);
    const planName = this.planName;
    const assigning = this._assignedPlanName !== null;

    this.isSaving = true;
    this._platformUserSubscriptionService
      .put({ companyGuid: this.user.companyGuid!, validTo })
      .subscribe({
        next: () => {
          this.isSaving = false;
          this._messageService.add({
            severity: 'success',
            summary: 'Sukces',
            detail: assigning
              ? `Przydzielono abonament ${planName}`
              : `Zmieniono datę ważności abonamentu ${planName}`,
          });
          const dialogResult: IAssignedSubscriptionResult = {
            subscriptionPlan: planName,
            subscriptionPlanActiveTo: validTo,
          };
          this._dynamicDialogRef.close(dialogResult);
        },
        error: (error) => {
          this.isSaving = false;
          if (assigning) {
            // Retrying must not apply the plan again
            this.mode = 'validTo';
          }
          this._messageService.add({
            severity: 'error',
            summary: assigning
              ? `Przydzielono abonament ${planName}, ale nie zapisano daty ważności`
              : 'Nie udało się zmienić daty ważności',
            detail: error?.error?.message ?? 'Spróbuj zapisać datę ponownie.',
          });
        },
      });
  }

  private endOfDay(date: Date): Date {
    const result = new Date(date);
    result.setHours(23, 59, 59, 0);
    return result;
  }
}
